/**
 * The catch-up player: a full-screen vertical story. One progress bar per director segment;
 * each segment drives a verified stage (moment scoreboard, period/clock, player, court) with big
 * captions. "Listen" narrates: server-generated voice, with captions when audio is unavailable.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { directorClock, directorTeamName, type DirectorAudio, type DirectorLanguage, type DirectorMoment, type DirectorResponse, type GameEvent } from '@clutch/core';
import { reducedMotion } from '../stage/Stage';
import { teamAccent } from '../ui/color';
import { captionPages, initPlayer, pageAt, playerReducer, playing, progressBars, readingMs } from './player';
import { captionAtAudioTime, chooseVoice, playbackDeadline, voiceLabel, waitForSpeech, type VoiceMode } from './voice';
import { kindLabel, strings } from './strings';
import { momentReason, momentVisual, scoringSide } from './moment-presentation';
import { MomentStage } from './MomentStage';
import { htmlLang, loc, pick, storyHasTranslation } from './i18n';
import type { Lens } from './moment-scene';
import { activeWordIndex, beatOf, decisiveMomentId, pageOfWord, validWords, wordPages, type Beat } from './show';
import { activeCue, activeCueByProgress, chapterFor, cueStarts, emphasisTreatment, usablePlan } from './showplan';
import { CueGraphic, cueEvent, cuePlayer } from './CueGraphic';
import { DirectorUnavailable } from './Unavailable';

export type StoryPlayerProps = {
  director: DirectorResponse;
  plays?: readonly GameEvent[];
  live?: boolean;
  /** Provider speech, cached after success. Unavailable audio can be retried. */
  loadSpeech?: () => Promise<DirectorAudio | undefined>;
  mediaUrl: (url: string) => string;
  onFinished?: () => void;
  onClose?: () => void;
  /** Language toggle etc. */
  controls?: ReactNode;
  /** Shown over the stage once the last segment ends. */
  end: ReactNode;
  /** Start narrating immediately (only after an explicit user tap elsewhere). */
  listenOnStart?: boolean;
  /** A flagship show stops on failed audio; only an explicit transcript choice uses a timer. */
  requireAudio?: boolean;
  /** Resume at this segment (language switch mid-story). */
  startAt?: number;
  onIndex?: (index: number) => void;
  /** Whose perspective frames the chapters (followed teams, familiarity, public author). */
  lens?: Lens;
  /** Studio actions in the header (Remix, perspective). */
  actions?: ReactNode;
  /** "Directed by AI · style · voice" credit under the captions. */
  credit?: ReactNode;
  /** Hold playback while a sheet is open over the player. */
  hold?: boolean;
  /** A one-shot overlay when a new take starts (e.g. a perspective flip). */
  banner?: ReactNode;
};

type Scene = {
  kind: 'intro' | 'moment' | 'outcome';
  eyebrow: string;
  title: string;
  clock?: string;
  home: number | undefined;
  away: number | undefined;
  moment?: DirectorMoment;
  final?: boolean;
};

function momentLabel(d: DirectorResponse, kind: DirectorMoment['kind'], l: DirectorLanguage) {
  const author = d.audience?.author;
  if (author && (kind === 'call' || kind === 'reaction' || kind === 'player')) {
    return pick({ en: `${author}’s ${kind}`, hi: `${author} ${kind === 'player' ? 'का खिलाड़ी' : kind === 'call' ? 'की कॉल' : 'की प्रतिक्रिया'}`, zh: `${author}的${kind === 'player' ? '关注球员' : kind === 'call' ? '预测' : '反应'}` }, l);
  }
  return kindLabel(kind, l);
}

export function sceneFor(d: DirectorResponse, index: number, l: DirectorLanguage): Scene {
  const seg = d.segments[index]!;
  const t = strings(l);
  const m = seg.momentId ? d.moments.find((x) => x.momentId === seg.momentId) : undefined;
  if (m) {
    const eyebrow = momentLabel(d, m.kind, l);
    return { kind: 'moment', eyebrow, title: loc(m.title, l), clock: directorClock(m.period, m.clockSec, l), home: m.homeScore, away: m.awayScore, moment: m };
  }
  const last = index === d.segments.length - 1 && index > 0;
  if (last) {
    const fin = d.asOf.status === 'final';
    return { kind: 'outcome', eyebrow: fin ? t.final : t.asOf, title: fin ? t.outcome : directorClock(d.asOf.period, d.asOf.clockSec, l), clock: fin ? t.final.toUpperCase() : directorClock(d.asOf.period, d.asOf.clockSec, l), home: d.asOf.homeScore, away: d.asOf.awayScore, final: fin };
  }
  const away = directorTeamName(d.game.away.tricode, l);
  const home = directorTeamName(d.game.home.tricode, l);
  const eyebrow = d.audience ? authorLabel(d.audience.author, l) : t.intro;
  return { kind: 'intro', eyebrow, title: pick({ en: `${away} at ${home}`, hi: `${away} बनाम ${home}`, zh: `${away}客场挑战${home}` }, l), home: undefined, away: undefined };
}

export function StoryPlayer(p: StoryPlayerProps) {
  if (!storyHasTranslation(p.director)) return <MissingStoryTranslation language={p.director.language} controls={p.controls} onClose={p.onClose} />;
  return <PlayableStory {...p} />;
}

export function MissingStoryTranslation({ language, controls, onClose }: { language: DirectorLanguage; controls?: ReactNode; onClose?: () => void }) {
  const t = strings(language);
  return <section className="cu cu--state" lang={htmlLang(language)} data-testid="story-translation-missing">
    {controls}<p role="status">{t.missingTranslation}</p>
    {onClose && <button className="btn btn--ghost" onClick={onClose}>{t.close}</button>}
  </section>;
}

const authorLabel = (author: string, l: DirectorLanguage) => pick({ en: `${author}’s story`, hi: `${author} की कहानी`, zh: `${author}的故事` }, l);

export function storySnapshotLabel(d: DirectorResponse, l: DirectorLanguage): string {
  if (d.asOf.status === 'final') return pick({ en: 'Game recap · Final', hi: 'मैच की कहानी · अंतिम', zh: '比赛回顾 · 已结束' }, l);
  const cutoff = directorClock(d.asOf.period, d.asOf.clockSec, l);
  return pick({ en: `Catch-up · through ${cutoff}`, hi: `मैच अपडेट · ${cutoff} तक`, zh: `比赛回顾 · 截至${cutoff}` }, l);
}

function PlayableStory(p: StoryPlayerProps) {
  const d = p.director;
  const l = d.language;
  const t = strings(l);
  const segs = d.segments;
  const lens = useMemo<Lens>(() => p.lens ?? (d.audience ? { who: d.audience.author } : {}), [p.lens, d.audience]);
  const estimates = useMemo(() => segs.map((s) => readingMs(s.text, l)), [segs, l]);
  const [st, dispatch] = useReducer(playerReducer, estimates, (e) => ({ ...initPlayer(e, p.requireAudio ? 'audio' : 'timer', p.startAt ?? 0), buffering: !!p.requireAudio }));
  const [voice, setVoice] = useState<VoiceMode>();
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceNote, setVoiceNote] = useState<string>();
  const [audioFailed, setAudioFailed] = useState(false);
  const [transcript, setTranscript] = useState(false);
  const strictAudio = !!p.requireAudio && !transcript;
  const [audioMs, setAudioMs] = useState(0);
  /** The segment epoch the audio clock belongs to: a fresh chapter never reads the last one's time. */
  const [clockEpoch, setClockEpoch] = useState(-1);
  const speechCache = useRef<Promise<DirectorAudio | undefined>>();
  const audioRef = useRef<HTMLAudioElement>(null);
  const finished = useRef(false);
  const heard = useRef(new Set<string>());
  const mounted = useRef(false);
  const pendingListen = useRef<AbortController>();
  const started = useRef(false);
  const playback = useRef<{ setPlaying: (active: boolean) => void }>();
  const state = useRef(st);
  state.current = st;
  // Props read inside effects through a ref: a parent re-render must never restart the voice.
  const props = useRef(p);
  props.current = p;

  useEffect(() => {
    props.current.onIndex?.(st.done ? 0 : st.index);
  }, [st.index, st.done]);

  // Clock.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      dispatch({ type: 'tick', dt: Math.min(250, now - last) });
      last = now;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (st.done && !finished.current) {
      finished.current = true;
      if (!props.current.requireAudio || segs.every((s) => heard.current.has(s.segmentId))) props.current.onFinished?.();
    }
    if (!st.done) finished.current = false;
  }, [st.done, segs]);

  // ── Voice ───────────────────────────────────────────────
  const stopVoices = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.removeAttribute('src');
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingListen.current?.abort();
      pendingListen.current = undefined;
      started.current = false;
      stopVoices();
    };
  }, [stopVoices]);

  const applyMode = useCallback((mode: VoiceMode) => {
    if (!mounted.current) return;
    if (mode.kind === 'captions' && props.current.requireAudio && !transcript) {
      stopVoices();
      setVoice(undefined);
      setAudioFailed(true);
      dispatch({ type: 'buffer', active: true });
      return;
    }
    setVoice(mode);
    setAudioMs(0);
    setVoiceNote(mode.kind === 'captions' ? t.noVoice : undefined);
    const durations = mode.kind === 'provider' ? segs.map((s) => mode.audio.segments.find((a) => a.segmentId === s.segmentId)?.durationMs ?? readingMs(s.text, l)) : estimates;
    dispatch({ type: 'driver', driver: mode.kind === 'captions' ? 'timer' : 'audio', durations });
  }, [segs, l, estimates, t.noVoice, stopVoices, transcript]);

  const listen = useCallback(async () => {
    if (pendingListen.current) return;
    const request = new AbortController();
    pendingListen.current = request;
    setAudioFailed(false);
    dispatch({ type: 'buffer', active: true });
    setVoiceBusy(true);
    setVoiceNote(undefined);
    let audio: DirectorAudio | undefined;
    const load = props.current.loadSpeech;
    if (load) {
      speechCache.current ??= Promise.resolve().then(load).catch(() => undefined);
      audio = await waitForSpeech(speechCache.current, request.signal);
    }
    if (!mounted.current || request.signal.aborted || pendingListen.current !== request) return;
    const mode = chooseVoice({ ...(audio ? { audio } : {}), segmentIds: segs.map((s) => s.segmentId) });
    if (mode.kind === 'captions') speechCache.current = undefined;
    setVoiceBusy(false);
    pendingListen.current = undefined;
    applyMode(mode);
  }, [segs, l, applyMode]);

  const mute = useCallback(() => {
    pendingListen.current?.abort();
    pendingListen.current = undefined;
    stopVoices();
    setVoiceBusy(false);
    setVoice(undefined);
    setAudioMs(0);
    dispatch({ type: 'driver', driver: 'timer', durations: estimates });
  }, [stopVoices, estimates]);

  useEffect(() => {
    if ((p.listenOnStart || strictAudio) && !started.current) {
      started.current = true;
      void listen();
    }
  }, [p.listenOnStart, strictAudio, listen]);

  // Speak the current segment whenever it (re)starts.
  const speaking = voice && voice.kind !== 'captions' && !st.done;
  useEffect(() => {
    if (!speaking || !voice) return;
    const i = st.index;
    const epoch = st.epoch;
    const seg = segs[i];
    if (!seg) return;
    setAudioMs(0);
    setClockEpoch(epoch);
    let active = true;
    let completed = false;
    const end = () => {
      if (!active || completed) return;
      completed = true;
      heard.current.add(seg.segmentId);
      deadline.cancel();
      dispatch({ type: 'segmentEnded', index: i, epoch });
    };
    const fallback = () => {
      if (!active || completed) return;
      completed = true;
      deadline.cancel();
      // A failure holds the same frame. A subsequent Listen retries the request.
      speechCache.current = undefined;
      applyMode({ kind: 'captions', reason: 'unavailable' });
    };
    const src = voice.kind === 'provider' ? voice.audio.segments.find((x) => x.segmentId === seg.segmentId) : undefined;
    const deadline = playbackDeadline(src ? src.durationMs + 15_000 : readingMs(seg.text, l) * 2.2, fallback);
    if (voice.kind === 'provider') {
      const a = audioRef.current;
      if (!a || !src) {
        fallback();
        return;
      }
      a.src = props.current.mediaUrl(src.url);
      a.currentTime = 0;
      a.onended = end;
      a.onerror = fallback;
      const updateClock = () => {
        if (!active || !playing(state.current)) return;
        const elapsedMs = a.currentTime * 1000;
        setAudioMs(elapsedMs);
        dispatch({ type: 'audioTime', index: i, epoch, elapsedMs });
      };
      a.ontimeupdate = updateClock;
      // Word-level captions need frame accuracy; timeupdate alone fires ~4×/s.
      let raf = 0;
      const poll = () => { if (!active) return; if (!a.paused) updateClock(); raf = requestAnimationFrame(poll); };
      raf = requestAnimationFrame(poll);
      let running = false;
      let attempt = 0;
      const session = { setPlaying: (value: boolean) => {
        if (!active || completed || running === value) return;
        running = value;
        const version = ++attempt;
        deadline.setPlaying(value);
        if (!value) { a.pause(); setAudioMs(a.currentTime * 1000); return; }
        void a.play().catch(() => {
          // pause()/src changes can reject an earlier play; they are not playback failures.
          if (active && running && version === attempt) fallback();
        });
      } };
      playback.current = session;
      session.setPlaying(playing(state.current));
      return () => {
        active = false;
        cancelAnimationFrame(raf);
        deadline.cancel();
        if (playback.current === session) playback.current = undefined;
        a.onended = null;
        a.ontimeupdate = null;
        a.onerror = null;
        a.pause();
      };
    }
  }, [speaking, voice, st.epoch, st.index, segs, l, applyMode]);

  // Hold / pause the voice with the story.
  const isPlaying = playing(st);
  useEffect(() => {
    playback.current?.setPlaying(isPlaying);
  }, [isPlaying, speaking, voice, st.epoch, st.index]);

  useEffect(() => {
    if (st.done) {
      pendingListen.current?.abort();
      pendingListen.current = undefined;
      setVoiceBusy(false);
      stopVoices();
    }
  }, [st.done, stopVoices]);

  // A sheet over the player holds it exactly like a finger on the stage.
  useEffect(() => {
    if (p.hold === undefined) return;
    dispatch({ type: p.hold ? 'hold' : 'release' });
  }, [p.hold]);

  // ── Input: tap zones + hold ─────────────────────────────
  const press = useRef<{ x: number; at: number; timer?: ReturnType<typeof setTimeout>; held: boolean }>();
  useEffect(() => () => clearTimeout(press.current?.timer), []);
  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a, input, video')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const cur = { x: (e.clientX - rect.left) / rect.width, at: Date.now(), held: false } as NonNullable<typeof press.current>;
    cur.timer = setTimeout(() => {
      cur.held = true;
      dispatch({ type: 'hold' });
    }, 220);
    press.current = cur;
  };
  const onUp = () => {
    const cur = press.current;
    press.current = undefined;
    if (!cur) return;
    clearTimeout(cur.timer);
    if (cur.held) return dispatch({ type: 'release' });
    dispatch({ type: cur.x < 0.3 ? 'prev' : 'next' });
  };
  const onCancel = () => {
    const cur = press.current;
    press.current = undefined;
    if (cur) clearTimeout(cur.timer);
    if (cur?.held) dispatch({ type: 'release' });
  };
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (audioFailed || props.current.hold) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight') dispatch({ type: 'next' });
      else if (e.key === 'ArrowLeft') dispatch({ type: 'prev' });
      else if (e.key === ' ') {
        e.preventDefault();
        dispatch({ type: 'toggle' });
      } else if (e.key === 'Escape') props.current.onClose?.();
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [audioFailed]);

  // ── Scene ────────────────────────────────────────────────
  const scene = sceneFor(d, st.index, l);
  const prevScene = st.index > 0 ? sceneFor(d, st.index - 1, l) : undefined;
  const seg = segs[st.index]!;
  const frac = st.durations[st.index] ? st.elapsedMs / st.durations[st.index]! : 0;
  let caption: string;
  let pageIndex = 0;
  let pageCount = 1;
  // Real word timings from the provider's alignment of THIS audio → karaoke captions.
  let karaoke: { words: { text: string }[]; active: number; offset: number } | undefined;
  let beat: Beat | undefined;
  let beatKey = '';
  const spokenMs = clockEpoch === st.epoch ? audioMs : 0;
  const timedSeg = voice?.kind === 'provider' && !st.done ? voice.audio.segments.find((x) => x.segmentId === seg.segmentId) : undefined;
  const timed = validWords(timedSeg?.words);
  if (timed) {
    const active = activeWordIndex(timed, spokenMs);
    const pages = wordPages(timed);
    pageCount = pages.length;
    pageIndex = pageOfWord(pages, Math.max(0, active));
    const [from, to] = pages[pageIndex]!;
    karaoke = { words: timed.slice(from, to), active: active - from, offset: from };
    caption = karaoke.words.map((w) => w.text).join(' ');
    beat = active >= 0 ? beatOf(timed[active]!.text, scene.moment) : undefined;
    beatKey = `${st.index}-${active}`;
  } else if (voice?.kind === 'provider' && !st.done) {
    const a = timedSeg;
    const c = captionAtAudioTime(a?.captions ?? [], spokenMs);
    const text = c?.text ?? (a?.captions.length ? '' : seg.text);
    const pages = captionPages(text);
    pageCount = pages.length;
    pageIndex = pageAt(pages, c ? Math.max(0, Math.min(1, (spokenMs - c.startMs) / Math.max(1, c.endMs - c.startMs))) : frac);
    caption = pages[pageIndex]!;
  } else {
    const pages = captionPages(seg.text);
    pageCount = pages.length;
    pageIndex = pageAt(pages, frac);
    caption = pages[pageIndex]!;
  }

  const away = d.game.away.tricode;
  const home = d.game.home.tricode;
  const style = { '--a': teamAccent(away), '--h': teamAccent(home) } as CSSProperties;
  const visual = momentVisual(d, scene.moment, p.plays);
  const scored = scoringSide(visual);
  const reason = momentReason(d, scene.moment, l);
  // Editorial cuts can jump minutes or run backward for a climax-first story. A score
  // animation across that gap would suggest invented intermediate game states.
  const adjacent = scene.moment && prevScene?.moment && scene.moment.seq === prevScene.moment.seq + 1;
  const label = voice ? voiceLabel(voice, l) : undefined;
  const decisive = !!scene.moment && scene.moment.momentId === decisiveMomentId(d) && d.moments.length > 1;
  // ── The producer's direction (model-authored showPlan), when present ──
  const plan = usablePlan(d);
  const chapter = chapterFor(plan, seg.segmentId);
  const treatment = emphasisTreatment(chapter?.emphasis);
  let cueIndex = -1;
  if (chapter?.visualCues.length) {
    const cues = chapter.visualCues;
    const hints = (cue: (typeof cues)[number]) => {
      const out: string[] = [];
      const who = cue.type === 'player' || cue.type === 'court-event' ? cuePlayer(cue, { d, plays: p.plays, moment: scene.moment }) : undefined;
      if (who) out.push(...who.name.split(/\s+/));
      const ev = cue.type === 'score-change' ? cueEvent(cue, { d, plays: p.plays, moment: scene.moment }) : undefined;
      if (ev) out.push(String(ev.homeScore), String(ev.awayScore));
      return out;
    };
    if (timed) cueIndex = activeCue(cueStarts(cues, timed, hints), activeWordIndex(timed, spokenMs));
    else if (voice?.kind === 'provider' && timedSeg) cueIndex = activeCueByProgress(cues.length, spokenMs / Math.max(1, timedSeg.durationMs));
    else cueIndex = activeCueByProgress(cues.length, frac);
  }
  const cue = chapter && cueIndex >= 0 ? chapter.visualCues[cueIndex] : undefined;
  const opening = !!plan && st.index === 0 && !st.done;
  const [lastBeat, setLastBeat] = useState<{ beat: Beat; key: string }>();
  useEffect(() => { if (beat && beatKey) setLastBeat({ beat, key: beatKey }); }, [beat, beatKey]);
  useEffect(() => { setLastBeat(undefined); }, [st.index, st.epoch]);

  if (audioFailed) return <section className="cu cu--state" lang={htmlLang(l)} data-testid="story-audio-error" data-index={st.index}>
    {p.controls}
    <DirectorUnavailable language={l} error={{ code: 'AUDIO_UNAVAILABLE', message: 'Narration could not play.', retriable: true }}
      onRetry={() => void listen()} onBack={p.onClose}
      onTranscript={() => {
        setTranscript(true); setAudioFailed(false); setVoice(undefined); setVoiceNote(undefined);
        dispatch({ type: 'driver', driver: 'timer', durations: estimates });
      }} />
  </section>;

  return (
    <div className={`sp sp--${scene.kind}${st.done ? ' is-done' : ''}${!isPlaying && !st.done ? ' is-paused' : ''}${(plan ? treatment.letterbox : decisive) && !st.done ? ' sp--decisive' : ''}${plan ? ` sp--plan sp--tone-${treatment.tone}` : ''}${cue?.type === 'silence' ? ' sp--silence' : ''}`} style={{ ...style, ...(plan ? { '--tempo': String(treatment.tempo) } : {}) } as CSSProperties} data-testid="story-player" data-index={st.index} data-lang={l} data-decisive={decisive || undefined} data-emphasis={chapter?.emphasis} data-plan={plan ? 'model' : undefined} aria-busy={st.buffering} lang={htmlLang(l)}>
      <audio ref={audioRef} preload="auto" />
      <div className="sp__wash" aria-hidden="true" data-side={scored ?? 'none'} />
      <header className="sp__top" aria-hidden={st.done}>
        {plan ? (
          <ol className="sp__arc" aria-label={chapter?.heading ?? ''}>
            {progressBars(st).map((f, i) => {
              const h = chapterFor(plan, segs[i]?.segmentId)?.heading;
              return <li key={i} className={i === st.index && !st.done ? 'is-on' : f >= 1 ? 'is-done' : ''} data-testid="chapter-label" aria-current={i === st.index && !st.done ? 'step' : undefined}><i style={{ transform: `scaleX(${f})` }} /><span>{h ?? ''}</span></li>;
            })}
          </ol>
        ) : (
          <ol className="sp__bars" aria-label={pick({ en: `Segment ${st.index + 1} of ${segs.length}`, hi: `भाग ${st.index + 1} / ${segs.length}`, zh: `第${st.index + 1}段，共${segs.length}段` }, l)}>
            {progressBars(st).map((f, i) => (
              <li key={i} data-testid="story-bar"><i style={{ transform: `scaleX(${f})` }} /></li>
            ))}
          </ol>
        )}
        <div className="sp__meta">
          <span className="sp__chip sp__chip--replay" data-testid="story-mode"><i aria-hidden="true" />{storySnapshotLabel(d, l)}</span>
          {label && !plan && <span className="sp__chip sp__chip--voice" data-testid="voice-label" data-kind={voice?.kind}>{voice?.kind === 'captions' ? '' : '◉ '}{label}</span>}
          {plan && voice?.kind === 'captions' && <span className="sp__chip sp__chip--voice" data-testid="voice-label" data-kind="captions">{label}</span>}
          {p.onClose && !st.done && <button className="sp__close" onClick={p.onClose} aria-label={t.close} data-testid="story-close">✕</button>}
        </div>
        {p.actions && <div className="sp__actions">{p.actions}</div>}
      </header>
      {p.banner}
      {!plan && lastBeat && !st.done && scene.moment && <BeatStamp key={lastBeat.key} beat={lastBeat.beat} d={d} m={scene.moment} l={l} />}
      <div className="sp__stage" aria-hidden={st.done} onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onCancel} onPointerLeave={onCancel} onContextMenu={(e) => e.preventDefault()}>
        {(plan ? treatment.letterbox : decisive) && !st.done && <div className="sp__letterbox" aria-hidden="true"><i /><i /></div>}
        <div key={`${st.index}-${st.epoch}`} className="sp__scene">
          <MomentStage d={d} l={l} plays={p.plays} lens={lens} scene={{ ...scene, ...(scene.moment ? { momentId: scene.moment.momentId } : {}) }} {...(adjacent && prevScene ? { prev: prevScene } : {})} />
        </div>
        {plan && chapter && !st.done && (
          <div className="sp__direction" key={`dir-${st.index}`}>
            {opening && (plan.thesis || plan.viewerReasonToCare) && (
              <div className="sp__opening" data-testid="show-thesis">
                {plan.viewerReasonToCare && <small>{plan.viewerReasonToCare}</small>}
                {plan.thesis && <p>{plan.thesis}</p>}
              </div>
            )}
            {chapter.onScreenText && <h2 className="sp__hero" data-testid="chapter-hero">{chapter.onScreenText}</h2>}
            {cue && <div className="sp__cue" key={`cue-${st.index}-${cueIndex}`}><CueGraphic cue={cue} d={d} l={l} plays={p.plays} {...(scene.moment ? { moment: scene.moment } : {})} heading={chapter.heading} treatment={treatment} /></div>}
          </div>
        )}
        <span className="sp__zone sp__zone--prev" aria-hidden="true" />
        <span className="sp__zone sp__zone--next" aria-hidden="true" />
        {!isPlaying && !st.done && <span className="sp__paused" aria-hidden="true">{st.buffering ? '…' : '❚❚'}</span>}
      </div>

      {!st.done && <footer className="sp__foot">
        {d.audience && <p className="sp__fine" data-testid="story-author">{authorLabel(d.audience.author, l)}</p>}
        {!plan && reason && <p className="sp__reason" data-testid="story-reason"><i aria-hidden="true" />{reason}</p>}
        {karaoke ? (
          <p className="sp__caption sp__caption--karaoke" data-testid="story-caption" data-timed="words" aria-label={caption} key={`${st.index}-${pageIndex}`}>
            {karaoke.words.map((w, i) => <span key={i} className={i === karaoke!.active ? 'is-now' : i < karaoke!.active ? 'is-said' : ''} data-testid="caption-word" data-active={i === karaoke!.active || undefined} aria-hidden="true">{w.text}</span>).flatMap((el, i) => (i ? [' ', el] : [el]))}
          </p>
        ) : <p className="sp__caption" data-testid="story-caption" aria-live="polite" key={`${st.index}-${pageIndex}`}>{caption}</p>}
        {pageCount > 1 && <ol className="sp__pages" aria-hidden="true">{Array.from({ length: pageCount }, (_, i) => <li key={i} className={i === pageIndex ? 'is-on' : ''} />)}</ol>}
        <div className="sp__controls">
          {voice && voice.kind !== 'captions' ? (
            <button className="sp__listen is-on" onClick={strictAudio ? () => dispatch({ type: 'toggle' }) : mute} data-testid="story-listen" aria-pressed="true"><Wave />{strictAudio ? (st.paused ? t.play : t.pause) : t.stop}</button>
          ) : (
            <button className="sp__listen" onClick={() => void listen()} disabled={voiceBusy} data-testid="story-listen" aria-pressed="false"><SpeakerIcon />{voiceBusy ? '…' : t.listen}</button>
          )}
          {p.controls}
          <button className="sp__nav" onClick={() => dispatch({ type: 'prev' })} aria-label={t.previous} data-testid="story-prev">‹</button>
          <button className="sp__nav" onClick={() => dispatch({ type: 'toggle' })} aria-label={st.paused ? t.play : t.pause} data-testid="story-toggle">{st.paused ? '▶' : '❚❚'}</button>
          <button className="sp__nav" onClick={() => dispatch({ type: 'next' })} aria-label={t.next} data-testid="story-next">›</button>
        </div>
        {plan ? (
          <>
            {voiceNote && <p className="sp__fine"><span role="status">{voiceNote}</span></p>}
            {chapter?.personalizationWhy && (
              <details className="sp__why" data-testid="chapter-why">
                <summary>{pick({ en: 'Why you’re seeing this', hi: 'आप यह क्यों देख रहे हैं', zh: '为什么给你看这个' }, l)}</summary>
                <p>{chapter.personalizationWhy}</p>
              </details>
            )}
          </>
        ) : (
          <p className="sp__fine">
            <span data-testid="generation-label">{d.narration?.mode === 'model' ? t.written : d.generation.mode === 'model' ? t.model : t.fallback}</span>
            {voiceNote && <span role="status">{voiceNote}</span>}
            {!voiceNote && <span>{t.tapHint}</span>}
          </p>
        )}
        {p.credit && <p className="sp__credit" data-testid="story-credit">{p.credit}</p>}
      </footer>}

      {st.done && (
        <div className="sp__end" data-testid="story-end">
          {(p.controls || p.onClose) && <div className="sp__controls">
            {p.controls}
            {p.onClose && <button className="sp__close" onClick={p.onClose} aria-label={t.close} data-testid="story-close">✕</button>}
          </div>}
          {d.audience && <p className="sp__fine" data-testid="story-author">{authorLabel(d.audience.author, l)}</p>}
          {plan?.endCard && (
            <section className="sp__endcard" data-testid="show-endcard" data-arc={plan.storyArc}>
              <h2>{plan.endCard.title}</h2>
              <p>{plan.endCard.text}</p>
              {plan.endCard.nextAction && <small data-testid="endcard-next">{plan.endCard.nextAction}</small>}
            </section>
          )}
          {p.end}
          <button className="sp__again" onClick={() => dispatch({ type: 'restart' })} data-testid="story-replay">↺ {t.replay}</button>
        </div>
      )}
    </div>
  );
}

/** Score tick-up from the previous scene's value (instant under reduced motion). */
export function useCountUp(value: number, from?: number): number {
  const [n, setN] = useState(from ?? value);
  useEffect(() => {
    if (from === undefined || from === value || reducedMotion()) {
      setN(value);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const dur = 650;
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setN(Math.round(from + (value - from) * eased));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    setN(from);
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, from]);
  return n;
}

/** A broadcast graphic stamped on the beat a word names: the score, the player, the swing. */
function BeatStamp({ beat, d, m, l }: { beat: Beat; d: DirectorResponse; m: DirectorMoment; l: DirectorLanguage }) {
  if (beat === 'score') return <div className="sp__beat sp__beat--score" data-testid="scene-beat" data-beat="score" aria-hidden="true"><b>{d.game.away.tricode}</b><strong>{m.awayScore}</strong><i>–</i><strong>{m.homeScore}</strong><b>{d.game.home.tricode}</b></div>;
  if (beat === 'player' && m.player) return <div className="sp__beat sp__beat--player" data-testid="scene-beat" data-beat="player" aria-hidden="true"><span>{m.player.name}</span><small>{directorClock(m.period, m.clockSec, l)}</small></div>;
  return <div className="sp__beat sp__beat--swing" data-testid="scene-beat" data-beat="swing" aria-hidden="true">{pick({ en: 'Lead swing', hi: 'बढ़त बदली', zh: '领先易主' }, l)}</div>;
}

function SpeakerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="currentColor" />
      <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function Wave() {
  return <span className="sp__wave" aria-hidden="true"><i /><i /><i /><i /></span>;
}
